import { DateTime } from '@ksyos/date-time';
import { FormSection } from '@ksyos/design-system';
import type {
    IEigenCorrectie,
    IOorkV1ProcedureCustomControlData,
} from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ServiceType } from '@common/enums';
import { getDisplayName } from '@common/utils/getDisplayName';
import { booleanAsJaNee } from '@common/utils/textUtils';
import { translateKey } from '@frontend/translate';
import { useEpisodeContext } from '@frontend/utils/index';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { OphthalmologyDropzoneRead } from '../../TFO/v1/OphthalmologyDropzoneRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { FormGroupRead } from '../../utils/v1/FormGroupRead';
import { OorkPermissionsRead } from './components/OorkPermissionsRead';
import { OorkV1ProcedureVisusFieldSetWrapper } from './OorkV1ProcedureVisusFieldSetWrapper';

export function OorkV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const { customControlData }: { customControlData: IOorkV1ProcedureCustomControlData } = props.procedure;

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.procedure?.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={(isOpened) => props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerOrganization?.name
                        ? props.process.procedure.performerOrganization.name
                        : undefined
                }
                practitionerName={
                    patient.authorization?.canSeePatientDetails && props.process.procedure?.performerPractitioner
                        ? getDisplayName(props.process.procedure.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={ServiceType.examination}
                showProcessIcon={true}
                title={getServiceLabel(props.process.code, 'procedure', props.process.name)}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <FormSection>
                    <FormGroupRead
                        label="ZorgDomein code"
                        value={customControlData.zorgdomein_code}
                    />
                    <FormGroupRead
                        label="E-mailadres betrokkene"
                        value={customControlData.email_betrokkene}
                    />
                    <FormGroupRead
                        label="Mobiel telefoonnummer betrokkene"
                        value={customControlData.mobileNumber}
                    />
                    <FormGroupRead
                        label="Telefoonnummer betrokkene"
                        value={customControlData.homePhoneNumber}
                    />
                    <FormGroupRead
                        label="BSN betrokkene"
                        value={
                            customControlData.bsn_betrokkene
                                ? customControlData.bsn_betrokkene
                                : patient.identifierSocialSecurity!
                        }
                    />
                </FormSection>

                <OorkPermissionsRead customControlData={props.procedure.customControlData} />

                <FormSection title={translateKey('carePlan.anamnese')}>
                    <FormGroupRead
                        label="Reden van keuring"
                        value={customControlData.overige_reden_van_keuring || customControlData.reden_van_keuring}
                    />
                    <FormGroupRead
                        label="Overige anamnese"
                        value={customControlData.overige_anamnese}
                    />
                    <FormGroupRead
                        label="Betrokkene in bezit van rijbewijs"
                        value={booleanAsJaNee(customControlData.betrokkene_bezit_rijbewijs)}
                    />
                    <FormGroupRead
                        label="Legitimatie betrokkene gezien"
                        value={booleanAsJaNee(customControlData.legitimatie_betrokkene_gezien)}
                    />
                    <FormGroupRead
                        label="Categorie rijbewijs"
                        value={customControlData.categorie_rijbewijs}
                    />
                </FormSection>

                <FormSection title={translateKey('diagnosticReport.examination')}>
                    <FormGroupRead
                        label="Datum van het onderzoek"
                        value={
                            customControlData.datum_onderzoek
                                ? DateTime.from(customControlData.datum_onderzoek).format('DD-MM-YYYY')
                                : ''
                        }
                    />
                    <FormGroupRead
                        label="Eigen correctie"
                        value={getEigenCorrectieAsString(customControlData.eigen_correctie)}
                    />

                    <OorkV1ProcedureVisusFieldSetWrapper
                        customControlData={customControlData}
                        isReadOnly={true}
                    />

                    <FormGroupRead
                        label="Refractie bij optimale correctie"
                        value={customControlData.refractie_optimale_correctie}
                    />
                    <FormGroupRead
                        label="Aanwijzingen voor defecten in het gezichtsveld aanwezig, volgens Donders"
                        value={booleanAsJaNee(customControlData.aanwijzingen_defecten_gezichtsveld)}
                    />
                    <FormGroupRead
                        label="Beschrijf de gezichtsvelddefecten"
                        value={customControlData.beschrijving_gezichtsvelddefecten}
                    />

                    <FormGroupRead
                        label="Sprake van diplopie"
                        value={booleanAsJaNee(customControlData.sprake_van_diplopie)}
                    />
                    <FormGroupRead
                        label="Betrokkene gebruikt hulpmiddel"
                        value={booleanAsJaNee(customControlData.betrokkene_gebruikt_hulpmiddel)}
                    />
                    <FormGroupRead
                        label="Diplopie is hinderlijk"
                        value={booleanAsJaNee(customControlData.diplopie_is_hinderlijk)}
                    />
                    <FormGroupRead
                        label="Toelichting diplopie"
                        value={customControlData.toelichting_diplopie}
                    />
                    <FormGroupRead
                        label="Gestoorde donkeradaptie"
                        value={customControlData.gestoorde_donkeradaptie}
                    />
                    <FormGroupRead
                        label="Toelichting donkeradaptie"
                        value={customControlData.toelichting_donkeradaptie}
                    />
                </FormSection>

                <FormSection title="Foto's">
                    <OphthalmologyDropzoneRead
                        files={props.procedure.customControlData.files}
                        id={props.procedure.id}
                    />
                </FormSection>
            </CustomControlCollapseBody>
        </ProcessContainer>
    );

    function getEigenCorrectieAsString(eigenCorrectie: IEigenCorrectie): string | null {
        const list = [];

        if (eigenCorrectie.bril) {
            list.push('bril');
        }
        if (eigenCorrectie.contactlenzen_overdag) {
            list.push('contactlenzen overdag');
        }
        if (eigenCorrectie.geen_eigen_correctie) {
            list.push('geen eigen correctie');
        }
        if (eigenCorrectie.nachtlenzen) {
            list.push('nachtlenzen');
        }

        if (list.length > 0) {
            const listAsString = list.join(', ');

            return listAsString[0].toUpperCase() + listAsString.slice(1);
        }

        return null;
    }
}
